import { CNS_API_BASE } from "@/config";
import {
  CNSInsight,
  CNSInsightPage,
  CNSEntityChangeHistory,
  CNSEntityChangeHistoryPage,
  JsonPatchOperation,
} from "@/models/cns";
import { throwOnProblemResponse } from "@/types/problem-detail";

export interface FetchInsightsPageParams {
  page?: number;
  size?: number;
  sort?: string[];
}

async function fetchInsightsPage(
  projectId: string,
  params: FetchInsightsPageParams = {},
): Promise<CNSInsightPage> {
  const query = new URLSearchParams();
  if (params.page != null) query.set("page", String(params.page));
  if (params.size != null) query.set("size", String(params.size));
  for (const s of params.sort ?? []) query.append("sort", s);
  const suffix = query.toString() ? `?${query.toString()}` : "";

  const res = await fetch(
    `${CNS_API_BASE}/projects/${projectId}/insights${suffix}`,
    { credentials: "include" },
  );
  if (!res.ok) await throwOnProblemResponse(res, "Failed to fetch insights");
  return res.json();
}

// Fetch insights for a project
export async function fetchInsights(projectId: string): Promise<CNSInsight[]> {
  const data = await fetchInsightsPage(projectId);
  return data.content || [];
}

/**
 * Fetches all project insights (paginating through Spring Page responses) and
 * returns VALIDATED insights scoped to a greenfield experiment.
 */
export async function fetchInsightsForExperiment(
  projectId: string,
  experimentPublicId: string,
): Promise<CNSInsight[]> {
  const collected: CNSInsight[] = [];
  let page = 0;
  let last = false;

  while (!last) {
    const data = await fetchInsightsPage(projectId, { page, size: 100 });
    const matches = (data.content ?? []).filter(
      (insight) =>
        insight.experimentPublicId === experimentPublicId &&
        insight.status === "VALIDATED",
    );
    collected.push(...matches);
    last = data.last;
    page += 1;
  }

  return collected;
}

// Fetch a single insight by ID
export async function fetchInsight(
  projectId: string,
  insightId: string
): Promise<CNSInsight> {
  const res = await fetch(
    `${CNS_API_BASE}/projects/${projectId}/insights/${insightId}`,
    { credentials: "include" }
  );
  if (!res.ok) await throwOnProblemResponse(res, "Failed to fetch insight");
  return res.json();
}

// Update user-editable Insight semantic fields using JSON Patch.
export async function updateInsight(
  projectId: string,
  insightId: string,
  patchOperations: JsonPatchOperation[]
): Promise<CNSInsight> {
  const res = await fetch(
    `${CNS_API_BASE}/projects/${projectId}/insights/${insightId}`,
    {
      method: "PATCH",
      credentials: "include",
      headers: { "Content-Type": "application/json-patch+json" },
      body: JSON.stringify(patchOperations),
    }
  );
  if (!res.ok) await throwOnProblemResponse(res, "Failed to update insight");
  return res.json();
}

// Fetch change history for an insight
export async function fetchInsightChangeHistory(
  projectId: string,
  insightId: string
): Promise<CNSEntityChangeHistory[]> {
  const res = await fetch(
    `${CNS_API_BASE}/projects/${projectId}/insights/${insightId}/change-history`,
    { credentials: "include" }
  );
  if (!res.ok) await throwOnProblemResponse(res, "Failed to fetch insight change history");
  const data: CNSEntityChangeHistoryPage = await res.json();
  return data.content || [];
}
